import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useProfileStore, type Patient } from "@/store/profile";
import authRequest from "@/api/auth-request";
import {
  tryResumePendingTask,
  clearPendingTaskResume,
  getPendingTaskResume,
  findPatientByPendingKey,
} from "@/constants/taskLinkSession";
import { SubmitButton } from "@/components/ui/SubmitButton";
import { cn } from "@/utils/cn";
import { PatientListPickerContent } from "@/components/account/PatientListPickerContent";

const loginSchema = z.object({
  userName: z.string().min(1, "Email is required").email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface PatientOption {
  firstName: string;
  lastName: string;
  patientID: string;
  facilityID: string;
  /** From GetUserPatients; use for Portal API. */
  facilityKey?: string;
  /** From GetUserPatients; use for Portal API. */
  patientKey?: string;
  officeName?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, completeAuth } = useAuth();
  const setProfileAuthInfo = useProfileStore((s) => s.setProfileAuthInfo);
  const setPatientAssociated = useProfileStore((s) => s.setPatientAssociated);
  const setCurrentPatient = useProfileStore((s) => s.setCurrentPatient);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [patientList, setPatientList] = useState<PatientOption[]>([]);
  const [showPatientSelect, setShowPatientSelect] = useState(false);
  const firstPatientButtonRef = useRef<HTMLButtonElement>(null);

  // When patient modal opens, move focus into it so Enter doesn't submit the login form
  useEffect(() => {
    if (showPatientSelect) {
      firstPatientButtonRef.current?.focus();
    }
  }, [showPatientSelect]);

  // Explain an expired-session redirect (flagged via ?sessionExpired=1 by the API 401 handler).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sessionExpired")) {
      toast.error("Your session expired. Please sign in again.");
      params.delete("sessionExpired");
      const qs = params.toString();
      window.history.replaceState({}, "", `/account/login${qs ? `?${qs}` : ""}`);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { userName: "", password: "" },
  });

  const handleLoginSubmit = async (data: LoginFormData) => {
    // Avoid double-submit when patient list is open (e.g. Enter key re-submits form)
    if (showPatientSelect) return;
    setIsSubmitting(true);
    try {
      const userData = await login(data.userName, data.password);
      if (!userData) {
        toast.error("Authentication failed.");
        return;
      }

      setProfileAuthInfo({
        firstName: (userData as { firstName?: string }).firstName ?? "",
        lastName: (userData as { lastName?: string }).lastName ?? "",
        nickName: (userData as { nickName?: string }).nickName ?? "",
        email: (userData as { email?: string }).email ?? "",
        ...(userData as Record<string, unknown>),
      });

      const patients = (
        await authRequest.request.getUserPatients(
          String((userData as { id?: number; userID?: number }).id ?? (userData as { userID?: number }).userID)
        )
      ).data as PatientOption[];

      if (!patients?.length) {
        completeAuth();
        clearPendingTaskResume();
        navigate("/", { replace: true });
        return;
      }

      setPatientList(patients);
      const pending = getPendingTaskResume();
      const linkedPatient =
        patients.length > 1 && pending?.patientKey?.trim()
          ? findPatientByPendingKey(patients, pending)
          : undefined;

      if (patients.length === 1 || linkedPatient) {
        const chosen = (linkedPatient ?? patients[0]) as PatientOption;
        setPatientAssociated(patients as unknown as Patient[]);
        setCurrentPatient(chosen as unknown as Patient);
        completeAuth();
        await tryResumePendingTask(navigate, chosen);
      } else {
        setShowPatientSelect(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSelectPatient = async (patient: PatientOption) => {
    setPatientAssociated(patientList as unknown as Patient[]);
    setCurrentPatient(patient as unknown as Patient);
    setShowPatientSelect(false);
    completeAuth();
    await tryResumePendingTask(navigate, patient);
  };

  return (
    <>
      <section className="card card-featured mb-4">
        <div className="card-body">
          <form onSubmit={handleSubmit(handleLoginSubmit)}>
            <span>
              <h3 className="label">PORTAL LOGIN</h3>
            </span>
            <div className="form-group">
              <label htmlFor="login-email" className="label f-weight-550">
                Email Address
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                className={cn("form-control form-control-sm", errors.userName && "border-red-500")}
                {...register("userName")}
              />
              {errors.userName && (
                <p className="mt-1 text-xs text-red-600">{errors.userName.message}</p>
              )}
            </div>
            <div className="form-group mb-4">
              <label htmlFor="login-password" className="label f-weight-550">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                className={cn("form-control form-control-sm", errors.password && "border-red-500")}
                {...register("password")}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>
            <SubmitButton
              isSubmitting={isSubmitting}
              loadingText="Validating..."
              className="btn btn-primary"
            >
              Login
            </SubmitButton>
            <Link to="/account/forgotpassword" className="hs-auth-link ml-3">
              Need help logging in?
            </Link>
          </form>
        </div>
      </section>

      {showPatientSelect && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E252B]/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Select Patient"
        >
          <div className="max-h-[80dvh] w-full max-w-xl overflow-auto rounded-xl border border-[#D5DBE4] bg-white p-4 shadow-[0_12px_32px_rgba(30,37,43,0.14)]">
            <PatientListPickerContent
              patients={patientList}
              getKey={(p) => `${p.facilityID}-${p.patientID}`}
              onSelect={onSelectPatient}
              firstItemRef={firstPatientButtonRef}
            />
          </div>
        </div>
      )}
    </>
  );
}
